import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

const steps = [
  { icon: 'ri-phone-line', title: 'Emergency Call', desc: 'Call us 24/7 at (209) 451-2288. We prioritize commercial clients to minimize business downtime.' },
  { icon: 'ri-search-eye-line', title: 'Commercial Assessment', desc: 'Our team evaluates the full scope of damage across your facility and creates a detailed restoration plan.' },
  { icon: 'ri-shield-line', title: 'Property Securing', desc: 'We immediately secure your commercial property to prevent further damage and protect your assets.' },
  { icon: 'ri-drop-line', title: 'Water & Damage Mitigation', desc: 'Industrial-grade extraction and drying equipment is deployed to stop damage progression fast.' },
  { icon: 'ri-building-2-line', title: 'Commercial Cleanup', desc: 'Full cleanup of affected areas including offices, warehouses, retail spaces, and common areas.' },
  { icon: 'ri-home-smile-line', title: 'Business Restoration', desc: 'We restore your commercial space to full operational condition as quickly as possible.' },
];

const reasons = [
  { icon: 'ri-time-line', title: 'Minimize Downtime', desc: 'We work around your schedule — nights, weekends, and holidays — to get your business back open fast.' },
  { icon: 'ri-award-line', title: 'Commercial Certified', desc: 'IICRC-certified technicians with extensive experience in large-scale commercial restoration projects.' },
  { icon: 'ri-file-list-3-line', title: 'Insurance & Documentation', desc: 'We provide detailed damage reports and work directly with commercial insurance carriers.' },
  { icon: 'ri-tools-line', title: 'Large-Scale Capability', desc: 'Our fleet of industrial equipment handles everything from small offices to large warehouses.' },
  { icon: 'ri-building-2-line', title: 'All Commercial Types', desc: 'Offices, retail stores, restaurants, warehouses, schools, medical facilities, and more.' },
  { icon: 'ri-thumb-up-line', title: 'Trusted by Businesses', desc: 'Merced businesses rely on Indoor-Restore Services for fast, professional commercial restoration.' },
];

export default function CommercialPage() {
  useEffect(() => {
    const siteUrl = 'https://mercedwaterrestoration.com';

    document.title = 'Commercial Restoration Services Merced CA | Indoor-Restore';

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'Large-scale water, fire, mold & storm damage restoration for commercial properties in Merced, CA. Offices, warehouses, retail & industrial facilities. 24/7 response. Call (209) 451-2288.');

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'commercial restoration Merced CA, commercial water damage, business restoration Central Valley, commercial mold remediation, office flood damage');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/services/commercial`);

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Commercial Restoration",
      "name": "Commercial Restoration Services in Merced, CA",
      "description": "Large-scale water, fire, mold, and storm damage restoration for commercial properties, offices, retail spaces, warehouses, and industrial facilities in Merced County with minimal business disruption.",
      "url": `${siteUrl}/services/commercial`,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Indoor-Restore Services",
        "telephone": "+12094512288",
        "email": "info@mercedwaterrestoration.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "1729 U St",
          "addressLocality": "Merced",
          "addressRegion": "CA",
          "postalCode": "95340",
          "addressCountry": "US"
        }
      },
      "areaServed": {
        "@type": "City",
        "name": "Merced",
        "containedInPlace": { "@type": "State", "name": "California" }
      },
      "availableChannel": {
        "@type": "ServiceChannel",
        "servicePhone": {
          "@type": "ContactPoint",
          "telephone": "+12094512288",
          "contactType": "Emergency Service",
          "availableLanguage": "English",
          "hoursAvailable": "24/7"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/35e03c4332c4886c6ad165f143ddee08.jpg"
            alt="Commercial Restoration Services Merced CA"
            className="w-full h-full object-cover object-top"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/65"></div>
        </div>
        <div className="relative z-10 text-center px-4 w-full max-w-5xl mx-auto py-24">
          <span className="inline-block bg-teal-500/30 border border-teal-400/50 text-teal-200 text-sm font-semibold px-4 py-1.5 rounded-full mb-5">
            Commercial & Industrial Restoration
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-5 leading-tight">
            Commercial Restoration<br />Services in Merced, CA
          </h1>
          <p className="text-xl text-white/85 mb-8 max-w-2xl mx-auto">
            Large-scale damage restoration for businesses, offices, retail spaces, and industrial facilities — with minimal disruption to your operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:2094512288" className="bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line mr-2"></i>Call (209) 451-2288
            </a>
            <a href="/#contact" className="bg-white text-teal-700 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              Get Free Estimate
            </a>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-5">Restoration Built Around Your Business</h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                When disaster strikes your commercial property, every hour of downtime costs money. <strong>Indoor-Restore Services</strong> understands the urgency — we deploy large crews with industrial equipment to restore your facility as fast as possible.
              </p>
              <p className="text-gray-600 leading-relaxed mb-5">
                From water and fire damage to mold and storm events, we handle all types of commercial restoration across Merced County and the Central Valley, working around your schedule to minimize business interruption.
              </p>
              <ul className="space-y-3">
                {[
                  'Office buildings & corporate campuses',
                  'Retail stores & shopping centers',
                  'Restaurants & food service facilities',
                  'Warehouses & industrial properties',
                  'Schools, churches & community centers',
                  'Medical & healthcare facilities',
                ].map(item => (
                  <li key={item} className="flex items-center gap-3 text-gray-700 text-sm">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl h-96">
              <img
                src="https://readdy.ai/api/search-image?query=restoration%20technicians%20in%20uniform%20setting%20up%20industrial%20air%20movers%20and%20dehumidifiers%20in%20a%20modern%20commercial%20office%20space%20with%20open%20floor%20plan%20after%20water%20damage%2C%20professional%20clean%20environment%2C%20bright%20lighting&width=800&height=600&seq=comm-overview-02&orientation=landscape"
                alt="Commercial water damage restoration equipment setup"
                className="w-full h-full object-cover object-top"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Commercial Restoration Process</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">Efficient, large-scale restoration designed to get your business back up and running with minimal downtime.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <div key={i} className="bg-white rounded-xl p-7 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${step.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <span className="text-3xl font-bold text-teal-100">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-white mb-4">Why Businesses Choose Indoor-Restore Services</h2>
            <p className="text-white/80 max-w-2xl mx-auto">We understand that commercial restoration is different — speed, scale, and minimal disruption are everything.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {reasons.map((r, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-7 border border-white/20 hover:bg-white/20 transition-all">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-5">
                  <i className={`${r.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{r.title}</h3>
                <p className="text-white/75 text-sm leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Need Commercial Restoration in Merced?</h2>
          <p className="text-gray-500 mb-8 text-lg">Call Indoor-Restore Services 24/7. We respond fast to protect your business and get you back to full operation.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:2094512288" className="bg-teal-600 text-white px-10 py-4 rounded-lg text-lg font-bold hover:bg-teal-700 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line mr-2"></i>(209) 451-2288
            </a>
            <a href="mailto:info@mercedwaterrestoration.com" className="border-2 border-teal-600 text-teal-600 px-10 py-4 rounded-lg text-lg font-bold hover:bg-teal-50 transition-all whitespace-nowrap cursor-pointer">
              Email Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
